import React, { useEffect, useMemo, useRef, useState } from "react";
import { Offcanvas, Button, Spinner } from "react-bootstrap";
import { playlistApi } from "./playlistMock";
import { Playlist, PlaylistVideo, fetchPlaylistById } from "./playlistData";
import { secondsToHms, formatTimestampToAgo } from "@/utils/ui/timeFunctions";
import { useAppSelector } from "@/store/index";
import { checkAdminConsolePermission } from "@/utils/permission";
import { fallbackThumb, Confirm } from "./playlistShared";

/* ───────────── Manage sheet (contents + drag reorder + pin + remove) ───────────── */
const ManageSheet: React.FC<{
  playlist: Playlist | null;
  isNewMembership: boolean;
  mediaTitle: string;
  mediaId: string;
  mediaThumbnail?: string;
  mediaDuration?: number;
  mediaCategory?: string;
  savedPosition?: number | null;
  onClose: () => void;
  onConfirm: (c: Confirm) => void;
  onSaveOrder: (
    playlistId: string,
    entry: {
      pid: string;
      position: number;
      title?: string;
      thumbnail?: string;
      duration?: number;
      category?: string;
    }
  ) => void;
}> = ({
  playlist,
  isNewMembership,
  mediaTitle,
  mediaId,
  mediaThumbnail,
  mediaDuration,
  mediaCategory,
  savedPosition,
  onClose,
  onConfirm,
  onSaveOrder,
}) => {
  const [order, setOrder] = useState<PlaylistVideo[]>([]);
  const [q, setQ] = useState("");
  const [hoveredRow, setHoveredRow] = useState<string | null>(null); // hover-reveal pin/remove
  const [detail, setDetail] = useState<Playlist | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dragIx = useRef<number | null>(null);
  const permissions = useAppSelector(
    (s) => s.users.singleCurrentUser?.permissions
  );
  const canEdit = checkAdminConsolePermission(permissions);

  const loadDetail = () => {
    if (!playlist) return;
    setLoading(true);
    setError(null);
    fetchPlaylistById(playlist.id)
      .then((p) => setDetail(p))
      .catch((e) => setError(e?.message || "Failed to load playlist"))
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    setDetail(null);
    setError(null);
    if (!playlist) return;
    loadDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playlist?.id]);
  const activePlaylist = detail ?? playlist;

  useEffect(() => {
    if (!activePlaylist) return;
    setQ("");
    const vids: PlaylistVideo[] = activePlaylist.videos ?? [];
    const existingIdx = mediaId ? vids.findIndex((v) => v.id === mediaId) : -1;
    const cur: PlaylistVideo =
      existingIdx >= 0
        ? { ...vids[existingIdx], isThisVideo: true }
        : {
            id: mediaId || "this",
            title: mediaTitle || "This video",
            // Secondary line shows "category · duration"; thumb shows the real media thumbnail.
            uploader: mediaCategory || "",
            publishedAt:
              mediaDuration != null ? secondsToHms(mediaDuration) : "",
            thumb: mediaThumbnail || undefined,
            isThisVideo: true,
          };
    const rest =
      existingIdx >= 0 ? vids.filter((_, i) => i !== existingIdx) : vids;
    const insertAt =
      savedPosition != null
        ? Math.min(Math.max(savedPosition - 1, 0), rest.length)
        : existingIdx >= 0
          ? existingIdx
          : isNewMembership
            ? 0
            : rest.length;
    const next = [...rest];
    next.splice(insertAt, 0, cur);
    setOrder(next);
  }, [
    activePlaylist,
    isNewMembership,
    mediaTitle,
    mediaId,
    mediaThumbnail,
    mediaDuration,
    mediaCategory,
    savedPosition,
  ]);

  const sorted = useMemo(
    () => [...order].sort((a, b) => Number(!!b.pinned) - Number(!!a.pinned)),
    [order]
  );

  const move = (from: number, to: number) =>
    setOrder(() => {
      const arr = [...sorted];
      const [m] = arr.splice(from, 1);
      arr.splice(to, 0, m);
      return arr;
    });
  return (
    <Offcanvas
      show={!!playlist}
      onHide={onClose}
      placement="end"
      container={typeof document !== "undefined" ? document.body : undefined}
      style={{ width: 750 }}
    >
      <Offcanvas.Header className="pb-2">
        <Offcanvas.Title className="fs-6">
          {activePlaylist?.name}
          <div
            className="text-muted d-flex align-items-center flex-wrap gap-1 mt-1"
            style={{ fontSize: 12, fontWeight: 400, textTransform: "none" }}
          ></div>
          <div
            className="text-muted mt-1"
            style={{ fontSize: 11, fontWeight: 400, textTransform: "none" }}
          >
            Total videos : <b>{sorted.length}</b> · Created by :{" "}
            <b>{activePlaylist?.createdBy}</b> · Updated by :{" "}
            <b>{activePlaylist?.mtBy}</b>
            {activePlaylist?.mt ? (
              <>
                {" "}
                · Updated at : <b>{formatTimestampToAgo(activePlaylist.mt)}</b>
              </>
            ) : null}
          </div>
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="d-flex flex-column p-0">
        <div
          className="alert alert-info d-flex align-items-center gap-2 mx-3 mb-2 px-2 py-1"
          role="alert"
          style={{ fontSize: 12 }}
        >
          <i className="bi bi-info-circle flex-shrink-0" />
          You can only reposition the current video. To reposition other
          videos, you need admin privileges.
        </div>
        <div className="px-3 pb-3 border-bottom">
          <input
            className="form-control form-control-sm"
            placeholder="Search in this playlist…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center text-muted small py-5">
            <Spinner animation="border" size="sm" className="mb-2" />
            Loading playlist…
          </div>
        ) : error ? (
          <div className="d-flex flex-column align-items-center justify-content-center text-danger small py-5 gap-2">
            <i className="bi bi-exclamation-triangle-fill fs-4" />
            <span>{error}</span>
            <Button size="sm" variant="outline-secondary" onClick={loadDetail}>
              Retry
            </Button>
          </div>
        ) : (
          <div className="flex-grow-1 overflow-auto">
            {sorted
              .map((v, i) => ({ v, i }))
              .filter(({ v }) =>
                v.title.toLowerCase().includes(q.trim().toLowerCase())
              )
              .map(({ v, i }) => {
                return (
                  <div
                    key={v.id}
                    draggable={!!v.isThisVideo}
                    onDragStart={
                      v.isThisVideo ? () => (dragIx.current = i) : undefined
                    }
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (dragIx.current !== null) {
                        move(dragIx.current, i);
                        dragIx.current = null;
                      }
                    }}
                    onMouseEnter={() => setHoveredRow(v.id)}
                    onMouseLeave={() =>
                      setHoveredRow((h) => (h === v.id ? null : h))
                    }
                    className="d-flex align-items-center gap-2 px-2 py-2"
                    style={{
                      cursor: v.isThisVideo ? "grab" : "default",
                      borderBottom: "1px solid var(--bs-border-color)",
                      // Current video — the only draggable row — gets a soft highlight.
                      borderLeft: `3px solid ${v.isThisVideo ? "#0d6efd" : "transparent"}`,
                      background: v.isThisVideo ? "#e7f1ff" : undefined,
                    }}
                  >
                    <div className="d-flex flex-column align-items-center gap-1">
                      <span
                        className=""
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          padding: "0 4px",
                          borderBottomRightRadius: 4,
                        }}
                      >
                        {i + 1}
                      </span>
                      {v.isThisVideo && (
                        <i
                          className="bi bi-grip-vertical text-secondary flex-shrink-0"
                          style={{ opacity: 0.6 }}
                          title="Drag to reorder this video"
                        />
                      )}
                    </div>
                    <span
                      className="position-relative flex-shrink-0 rounded overflow-hidden bg-light"
                      style={{ width: 72, height: 40 }}
                    >
                      <img
                        src={v.thumb || fallbackThumb(v.id)}
                        alt=""
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = fallbackThumb(v.id);
                        }}
                      />
                    </span>
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                      <div
                        className=""
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          lineHeight: 1.1,
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {v.title}
                      </div>
                      <div
                        className="text-muted text-truncate"
                        style={{ fontSize: 11 }}
                      >
                        {v.uploader} · {v.publishedAt}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}
        <div className="p-3 border-top d-flex justify-content-between">
          {canEdit ? (
            <Button
              variant="outline-secondary"
              disabled={!playlist}
              onClick={() => {
                if (!playlist) return;
                const adminUrl = import.meta.env.VITE_ADMIN_URL ?? "";
                window.open(
                  `${adminUrl}/playlist/custom/${playlist.id}`,
                  "_blank"
                );
              }}
            >
              Edit
            </Button>
          ) : (
            <span />
          )}
          <Button
            onClick={() => {
              if (playlist) {
                playlistApi.reorderPlaylist(
                  playlist.id,
                  sorted.map((v) => v.id)
                );
                const idx = sorted.findIndex((v) => v.isThisVideo);
                if (idx >= 0) {
                  onSaveOrder(playlist.id, {
                    pid: playlist.id,
                    position: idx + 1,
                    title: mediaTitle,
                    thumbnail: mediaThumbnail,
                    duration: mediaDuration,
                    category: mediaCategory,
                  });
                }
              }
              onClose();
            }}
          >
            Save Changes
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default ManageSheet;
